import React, { useState, useEffect, lazy } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import SvgIcon from 'globalcomponents/SvgIcon/Base';
import PdfView from './PdfViewer';
import TopActions from './TopActions';
import Violations from '../Dashboard/Notfications';
import { pageUrls, ROLES } from 'globalconfig/constants/keys';
import { UI_DATE_FORMAT_WITH_TIME_UTC, UI_DATE_FORMAT } from 'globalconfig/constants/static';
import { getLogsInfoDisplay, hasRestrictedRole } from 'globalutils/fmUtils';
import { mediaUrls } from 'globalconfig/constants/mediaUrls';

const layout = window?.$environment?.CURRENT_LAYOUT;
const Map = lazy(() => import(`globalcomponents/GoogleMap/${layout}`));
const Label = lazy(() => import(`globalcomponents/UI/Label/${layout}`));
const Link = lazy(() => import(`globalcomponents/UI/Link`));
const Table = lazy(() => import(`globalcomponents/UI/Table/${layout}`));
const Message = lazy(() => import(`globalcomponents/UI/Message/${layout}`));

const Index = ({ theme, filters, ...contentProps }) => (
  <Box style={{ ...theme.palette.contentStyle }}>
    <Component {...contentProps} />
  </Box>
);
export default Index;

const Component = (props) => {
  const {
    apiStatus,
    callback,
    fetchLogBookView,
    getLogbookEvents,
    updateShowEvents,
    i18n,
    history,
    drivers,
    dates,
    unSignedDates,
    logBook,
    handleLogBookStatus,
    clearApiStatus,
    currentDriver,
    breadCrumbForMap,
    viewApiId,
    fetchLogbookBreadcrumb,
    fetchLogbookBreadcrumbApiId,
    dailyLogDownloadData,
    showMap,
    Styled,
    violationListing,
    getDocumentToken,
    formValues,
    setFormValues,
    topAction,
    newDate,
    setNewDate,
    mapRequired,
    regeneratePdf,
  } = props;

  const [currentIndex, setCurrentIndex] = useState(0);
  const match = useParams();

  const selectedLog = logBook?.selectedDailyLog;

  useEffect(() => {
    if (selectedLog?.vehicleId) {
      fetchLogbookBreadcrumb({
        apiId: fetchLogbookBreadcrumbApiId,
        data: {
          id: selectedLog.vehicleId,
          day: selectedLog.day,
        },
      });
    }
  }, [selectedLog?.vehicleId]);

  useEffect(() => {
    setFormValues({
      driverId: selectedLog?.driverId,
      driverName: selectedLog?.driverName,
      type: 'DAILY_LOG',
    });
  }, [selectedLog?.driverId]);

  const isMapRequired = mapRequired === undefined || mapRequired;

  const generateRows = () =>
    (logBook?.violationsListDto || []).map((item) => {
      const formattedDate = moment(item.date).format(UI_DATE_FORMAT_WITH_TIME_UTC);
      return {
        ...item,
        date: formattedDate,
        description: (
          <div id={item?.id}>
            <div style={{ fontWeight: 'bold' }}>{item.description}</div>
            <div style={{ fontSize: '14px' }}>{formattedDate} (UTC)</div>
          </div>
        ),
      };
    });

  const renderNavigationLinks = () => {
    const logs = logBook?.dailyLogs || [];
    const navigateToLog = (newIndex) => {
      setCurrentIndex(newIndex);
      const date = logs[newIndex]?.day || newDate;
      fetchLogBookView({
        apiId: viewApiId,
        callback,
        data: { data: { date, driverId: currentDriver.value } },
      });
      fetchLogbookBreadcrumb({
        apiId: 'fetchLogbookBreadcrumb',
        data: {
          id: selectedLog?.vehicleId,
          day: date,
        },
      });
    };

    return (
      <div className="linkContainer">
        <div className="prev">
          <Link className="uppercase" onClick={() => currentIndex < logs.length - 1 && navigateToLog(currentIndex + 1)}>
            {i18n.t('common.previous')}
          </Link>
        </div>
        <div className="next">
          <Link className="uppercase" onClick={() => currentIndex > 0 && navigateToLog(currentIndex - 1)}>
            {i18n.t('common.next')}
          </Link>
        </div>
      </div>
    );
  };

  const renderActionButtons = () => (
    <div className="linkContainer commonLinks logEditButtons">
      <div>
        <Link
          className="uppercase logEdit logEditButton"
          onClick={() => {
            const { idPub, id, status } = selectedLog || {};
            const url =
              status === 'SIGNED'
                ? hasRestrictedRole([ROLES.VIEW_ONLY_FM])
                  ? pageUrls.signedLogsView.replace(':id', idPub)
                  : pageUrls.logsEdit.replace(':id', idPub)
                : pageUrls.logsView.replace(':id', id);
            history.push(url);
          }}
        >
          {hasRestrictedRole([ROLES.VIEW_ONLY_FM]) ||
            (window.$environment.SERVICE_PROVIDER === 'spireon' && selectedLog?.status !== 'SIGNED') ? (
            <>{i18n.t('common.view')}</>
          ) : (
            <>
              <SvgIcon wrpClass='icon-spi' value={mediaUrls.logEdit} />
              {i18n.t('common.edit')}
            </>
          )}
        </Link>
      </div>
      {
        <div>
          <Link
            className="uppercase dailyDocument logEditButton"
            href={`${pageUrls.dailyDocuments}?driver=${selectedLog?.driverId}&day=${moment(selectedLog?.day).format(
              UI_DATE_FORMAT
            )}`}
          >
            <SvgIcon wrpClass="icon-spi" value={mediaUrls.viewDocument} />
            {i18n.t('logs.viewDocument')}
          </Link>
        </div>
      }
      <div>
        <Link
          className="uppercase history logEditButton"
          onClick={() => {
            history.push(pageUrls.logsHistory.replace(':id', selectedLog?.idPub));
          }}
        >
          <SvgIcon wrpClass="icon-spi" value={mediaUrls.historyLogs} />
          {i18n.t('common.history')}
        </Link>
      </div>
    </div>
  );

  return (
    <Styled.LogBookContainer>
      {topAction && <TopActions {...{ ...props }} />}

      {layout !== 'SpireonNew' && (
        <Label className="logsInfoDisplay">{getLogsInfoDisplay(i18n, selectedLog?.driverName, selectedLog?.day)}</Label>
      )}

      <div className="displayContainer">
        <div className="linkButtonsContainer">
          {renderNavigationLinks()}
          {renderActionButtons()}
        </div>

        <div className="leftWrapper">
          <div className="pdfViolationsContainer">
            {!!logBook.dailyLogs.length && (
              <PdfView
                {...{
                  currentIndex,
                  setCurrentIndex,
                  list: logBook.dailyLogs,
                  Styled,
                  i18n,
                  driver: currentDriver.value,
                  date: newDate,
                  documentToken: logBook?.documentToken,
                  updateShowEvents,
                  getLogbookEvents: (idPub) =>
                    getLogbookEvents({
                      apiId: 'LoadMoreEvents',
                      callback,
                      data: { id: idPub },
                    }),
                  fetchLogBookView: (date) =>
                    fetchLogBookView({
                      apiId: viewApiId,
                      callback,
                      data: { data: { date, driverId: currentDriver.value } },
                    }),
                  fetchLogbookBreadcrumb,
                  id: selectedLog?.vehicleId,
                  eventsData: logBook?.eventsData,
                  getDocumentToken,
                  selectedDailyLog: selectedLog,
                  regeneratePdf: ({ date, callbackFuntion }) => {
                    // Use the date passed from PdfViewer, fallback to newDate if not provided
                    const dateToUse = date || newDate;
                    if (!dateToUse || !currentDriver?.value) {
                      callback('error', 'Date and driver ID are required for PDF regeneration.');
                      return;
                    }
                    regeneratePdf({
                      apiId: 'fetchLogbookBreadcrumb',
                      callback,
                      data: { data: { date: dateToUse, driverId: currentDriver.value } },
                      newCallback: () => {
                        fetchLogBookView({
                          apiId: 'fetchLogBookView',
                          callback,
                          data: { data: { driverId: currentDriver.value, date: dateToUse } },
                          history,
                        });
                        callbackFuntion && callbackFuntion();
                      },
                    });
                  },
                }}
              />
            )}

            <div className="violationsContainer">
              {violationListing ? (
                <Violations potentialViolations={logBook?.violationsListDto || []} />
              ) : (
                <Styled.ViolationContainer>
                  <div className="heading">{i18n.t('logs.violations')}</div>
                  {logBook?.violationsListDto?.length ? (
                    <Table
                      type="client"
                      columns={[
                        {
                          id: 'description',
                          numeric: false,
                          disablePadding: true,
                          label: i18n.t('common.description'),
                          needSorting: false,
                        },
                      ]}
                      data={generateRows()}
                      actions={[]}
                      itemsPerPage={window.$environment.SERVICE_PROVIDER === 'spireon' ? logBook.violationsListDto.length : 10}
                      emptyMessage={''}
                      i18n={i18n}
                      noPagination={window.$environment.SERVICE_PROVIDER === 'spireon'}
                      isViolationsTable
                    />
                  ) : (
                    <Message message={i18n.t('logs.noViolation')} type="info" />
                  )}
                </Styled.ViolationContainer>
              )}

              <Styled.MapContainer>
                <Styled.MapWrapper>
                  {isMapRequired && <Map height="350px" type="logBookBreadCrumbMap" data={breadCrumbForMap} />}
                </Styled.MapWrapper>
              </Styled.MapContainer>
            </div>
          </div>
        </div>

        <div className="rightSide" />
      </div>
    </Styled.LogBookContainer>
  );
};

Component.defaultProps = {
  drivers: {},
};

Component.propTypes = {
  apiStatus: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  manageLogList: PropTypes.object,
  logBook: PropTypes.object.isRequired,
  handleLogBookStatus: PropTypes.func.isRequired,
  clearApiStatus: PropTypes.func.isRequired,
  fetchLogbookBreadcrumb: PropTypes.func.isRequired,
  breadCrumbForMap: PropTypes.array.isRequired,
};
